'use client';

import type { CSSProperties, ReactNode } from 'react';

import { Comments } from '@ps/features/video-player/components/comments/Comments';
import type {
  BasicUserProfileInfo,
  PopClipVideoInfo,
} from '@ps/features/video-player/functions';
import { cn } from '@ps/ui/utils';

import { DESKTOP_CARD_HEIGHT, DESKTOP_COLUMN_WIDTH } from './playerLayout';

interface Props {
  /**
   * The 9:16 video column's content — the host's `VideoClipsProvider` wrapping
   * its deck (or single clip), plus any absolutely-positioned overlay it owns
   * (e.g. the clip access gate).
   */
  children: ReactNode;
  /**
   * The clip whose comments are open — `null` collapses the desktop panel to
   * zero width and unmounts the mobile sheet.
   */
  commentsFor: PopClipVideoInfo | null;
  currentUserId?: string;
  currentUserMeta: BasicUserProfileInfo | null;
  onCloseComments: () => void;
  /** Accessible name for the card, rendered screen-reader-only. */
  label?: string;
  className?: string;
}

/**
 * The bio clip player card — a 9:16 video column with the comments panel beside
 * it on desktop and as a bottom sheet on mobile. Shared by the two visitor
 * players ({@link SeriesClipsPlayer}'s swipe deck and {@link ClipVideoPage}'s
 * single clip) so they stay visually identical. Layout-agnostic: the HOST owns the
 * shell around it (the full-screen backdrop, centering, the route mount), the
 * deck inside it, and every action its clips dispatch.
 */
export function ClipPlayerCard({
  children,
  commentsFor,
  currentUserId,
  currentUserMeta,
  onCloseComments,
  label,
  className,
}: Props) {
  const showComments = commentsFor != null;

  return (
    <div
      style={{ '--player-card-h': DESKTOP_CARD_HEIGHT } as CSSProperties}
      className={cn(
        'bg-surface-2 relative flex max-sm:h-full max-sm:w-full sm:h-[var(--player-card-h)] sm:overflow-hidden sm:rounded-2xl sm:shadow-2xl',
        className,
      )}
    >
      {/* Video column — the host's provider must wrap ONLY the deck so its
        ResizeObserver measures this 9:16 column, and the comments panel stays a
        flex-ROW sibling beside it (matching VideoModal). Nesting comments inside
        the provider's flex-col wrapper would stack them under the full-height
        column, where they never become visible. */}
      <div
        data-clip-preview-container=""
        className="relative flex h-full flex-col max-sm:w-full sm:aspect-[9/16]"
      >
        {children}
      </div>

      {/* Desktop comments side panel — width tracks the video column
        (height × 9/16); collapses to 0 when closed. */}
      <div
        className={cn(
          'hidden h-full shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out sm:block',
          showComments ? 'border-raise/10 border-l' : 'w-0',
        )}
        style={{ width: showComments ? DESKTOP_COLUMN_WIDTH : 0 }}
      >
        {commentsFor ? (
          <div className="h-full" style={{ width: DESKTOP_COLUMN_WIDTH }}>
            <Comments
              video={commentsFor}
              currentUserId={currentUserId}
              currentUserMeta={currentUserMeta}
              onClose={onCloseComments}
              className="h-full"
            />
          </div>
        ) : null}
      </div>

      {/* Mobile comments bottom sheet + scrim. */}
      {commentsFor ? (
        <>
          <div
            className="bg-scrim/50 absolute inset-0 z-40 sm:hidden"
            onClick={onCloseComments}
          />
          <div className="animate-in slide-in-from-bottom absolute inset-x-0 bottom-0 z-50 h-[60%] duration-300 sm:hidden">
            <Comments
              video={commentsFor}
              currentUserId={currentUserId}
              currentUserMeta={currentUserMeta}
              onClose={onCloseComments}
              className="h-full rounded-t-2xl"
            />
          </div>
        </>
      ) : null}

      {label ? <span className="sr-only">{label}</span> : null}
    </div>
  );
}
